// client/src/utils/buildPlayerStatsPayload.ts
//
// Convierte un ParsedTournament (stats crudos) en un row de player_stats
// listo para UPSERT a Supabase.

import { ParsedTournament } from './pokerCraftParser';
import { derivedStats } from './pokerStats/derived';
import { computeStakeRange } from './computeStakeRange';

export interface PlayerStatsRow {
  user_id: string;
  tournament_id: string;
  date: string;             // YYYY-MM-DD
  format: string | null;
  stake_range: string;
  buy_in: number;

  // Preflop
  hands_played: number;
  vpip: number;
  pfr: number;
  pfr_vpip_ratio: number;
  threebet: number;
  fold_to_3bet: number;
  ats: number;

  // Postflop aggression
  af: number;
  afq: number;

  // CBet
  cbet_flop: number;
  cbet_flop_success: number;
  cbet_turn: number;
  cbet_turn_success: number;

  // Fold / call to CBet
  fold_to_cbet_flop: number;
  fold_to_cbet_turn: number;
  call_cbet_flop: number;
  call_cbet_turn: number;

  // Donk
  donk_flop: number;
  donk_turn: number;
  donk_river: number;

  // Showdown
  wtsd: number;
  wwsf: number;
  wsd: number;

  avg_stack_bb: number;

  // New — raw counts in JSON for future aggregation
  extended_stats: Record<string, any>;
}

/**
 * Construye un row listo para insert/upsert en player_stats.
 *
 * El caller es responsable de verificar que el user dio consent ANTES
 * de llamar esta función.
 */
export function buildPlayerStatsPayload(
  userId: string,
  t: ParsedTournament,
  buyInOverride?: number
): PlayerStatsRow | null {
  // Guard: torneos sin handsPlayed no tienen valor estadístico
  if (!t.handsPlayed || t.handsPlayed < 1) return null;

  const d = derivedStats(t);
  const buyIn = buyInOverride ?? 0;

  // Build the extended_stats blob with raw counts for new stats
  const extended: Record<string, any> = {};

  // Fase 1: cbet flop matchups
  if (t.cbetFlopMatchups && Object.keys(t.cbetFlopMatchups).length > 0) {
    extended.cbetFlopMatchups = t.cbetFlopMatchups;
  }

  // Fase 2: fold to 3bet allin/non-allin split
  if (typeof t.foldTo3betAllinOpp === 'number' && t.foldTo3betAllinOpp > 0) {
    extended.foldTo3betAllin = {
      opp: t.foldTo3betAllinOpp,
      count: t.foldTo3betAllinCount || 0,
    };
  }
  if (typeof t.foldTo3betNonAllinOpp === 'number' && t.foldTo3betNonAllinOpp > 0) {
    extended.foldTo3betNonAllin = {
      opp: t.foldTo3betNonAllinOpp,
      count: t.foldTo3betNonAllinCount || 0,
    };
  }

  // Fase 3: RFI by position
  if (t.rfiByPosition && Object.keys(t.rfiByPosition).length > 0) {
    extended.rfiByPosition = t.rfiByPosition;
  }

  // Fase 4: 3bet / fold-to-3bet by matchup
  if (t.threebetByMatchup && Object.keys(t.threebetByMatchup).length > 0) {
    extended.threebetByMatchup = t.threebetByMatchup;
  }
  if (t.foldTo3betByMatchup && Object.keys(t.foldTo3betByMatchup).length > 0) {
    extended.foldTo3betByMatchup = t.foldTo3betByMatchup;
  }

  return {
    user_id: userId,
    tournament_id: t.tournamentId,
    date: t.date,
    format: t.format || null,
    stake_range: computeStakeRange(buyIn),
    buy_in: buyIn,

    hands_played: t.handsPlayed,
    vpip: d.vpip,
    pfr: d.pfr,
    pfr_vpip_ratio: d.pfrVpipRatio,
    threebet: d.threebet,
    fold_to_3bet: d.foldTo3bet,
    ats: d.ats,

    af: d.af,
    afq: d.afq,

    cbet_flop: d.cbetFlop,
    cbet_flop_success: d.cbetFlopSuccess,
    cbet_turn: d.cbetTurn,
    cbet_turn_success: d.cbetTurnSuccess,

    fold_to_cbet_flop: d.foldToCbetFlop,
    fold_to_cbet_turn: d.foldToCbetTurn,
    call_cbet_flop: d.callCbetFlop,
    call_cbet_turn: d.callCbetTurn,

    donk_flop: d.donkFlop,
    donk_turn: d.donkTurn,
    donk_river: d.donkRiver,

    wtsd: d.wtsd,
    wwsf: d.wwsf,
    wsd: d.wsd,

    avg_stack_bb: 0, // Not currently computed at tournament level; placeholder

    extended_stats: extended,
  };
}
